import { api } from './index';
import { 
  BuildingUtilityGroup, 
  BuildingUtility,
  BuildingUtilityBooking,
  CreateUtilityGroupRequest, 
  UpdateUtilityGroupRequest,
  PaginatedResponse,
  PaginationParams 
} from '../types';

export const buildingUtilityApi = {
  // Utility Groups
  getUtilityGroups: async (buildingId: string, params?: PaginationParams) => {
    const response = await api.get<PaginatedResponse<BuildingUtilityGroup>>('/building-utilities/groups', { 
      params: { buildingId, ...params } 
    });
    return response.data;
  },

  getUtilityGroup: async (id: string) => {
    const response = await api.get<BuildingUtilityGroup>(`/building-utilities/groups/${id}`);
    return response.data;
  },

  createUtilityGroup: async (data: CreateUtilityGroupRequest) => {
    const response = await api.post<BuildingUtilityGroup>('/building-utilities/groups', data);
    return response.data;
  },

  updateUtilityGroup: async (id: string, data: UpdateUtilityGroupRequest) => {
    const response = await api.patch<BuildingUtilityGroup>(`/building-utilities/groups/${id}`, data);
    return response.data;
  },

  deleteUtilityGroup: async (id: string) => {
    const response = await api.delete(`/building-utilities/groups/${id}`);
    return response.data;
  },

  createDefaultUtilityGroups: async (buildingId: string) => {
    const response = await api.post(`/building-utilities/groups/create-default/${buildingId}`);
    return response.data;
  },

  toggleUtilityGroupStatus: async (id: string) => {
    const response = await api.patch(`/building-utilities/groups/${id}/toggle-status`);
    return response.data;
  },

  // Utilities
  getUtilities: async (buildingId: string, params?: PaginationParams & { groupId?: string }) => {
    const response = await api.get<PaginatedResponse<BuildingUtility>>('/building-utilities/utilities', { 
      params: { buildingId, ...params } 
    });
    return response.data;
  },

  getUtility: async (id: string) => {
    const response = await api.get<BuildingUtility>(`/building-utilities/utilities/${id}`);
    return response.data;
  },

  createUtility: async (data: any) => {
    const response = await api.post<BuildingUtility>('/building-utilities/utilities', data);
    return response.data;
  },

  updateUtility: async (id: string, data: any) => {
    const response = await api.patch<BuildingUtility>(`/building-utilities/utilities/${id}`, data);
    return response.data;
  },

  deleteUtility: async (id: string) => {
    const response = await api.delete(`/building-utilities/utilities/${id}`);
    return response.data;
  },

  // Bookings
  getBookings: async (buildingId: string, queryString?: string) => {
    const url = queryString 
      ? `/building-utilities/bookings?${queryString}`
      : `/building-utilities/bookings?buildingId=${buildingId}`;
    const response = await api.get<PaginatedResponse<BuildingUtilityBooking>>(url);
    return response.data;
  },

  getBooking: async (id: string) => {
    const response = await api.get<BuildingUtilityBooking>(`/building-utilities/bookings/${id}`);
    return response.data;
  },

  createBooking: async (data: any) => {
    const response = await api.post<BuildingUtilityBooking>('/building-utilities/bookings', data);
    return response.data;
  },

  updateBooking: async (id: string, data: any) => {
    const response = await api.patch<BuildingUtilityBooking>(`/building-utilities/bookings/${id}`, data);
    return response.data;
  },

  deleteBooking: async (id: string) => {
    const response = await api.delete(`/building-utilities/bookings/${id}`);
    return response.data;
  },

  previewCollectBookingDeposit: async (
    id: string,
    data: {
      collectAmount?: number;
      paymentMethod?: 'CASH' | 'BANK_TRANSFER';
      projectBankAccountId?: string;
      paymentDate?: string;
    } = {},
  ) => {
    const response = await api.post<{ html: string }>(
      `/building-utilities/bookings/${id}/collect-deposit/preview`,
      data,
    );
    return response.data;
  },

  collectBookingDeposit: async (
    id: string,
    data: {
      collectAmount?: number;
      paymentMethod?: 'CASH' | 'BANK_TRANSFER';
      projectBankAccountId?: string;
      paymentDate?: string;
    } = {},
  ) => {
    const response = await api.patch<
      BuildingUtilityBooking & { apartmentLedgerEntryId?: string | null; journalNumber?: string | null }
    >(`/building-utilities/bookings/${id}/collect-deposit`, data);
    return response.data;
  },

  previewRefundBookingDeposit: async (
    id: string,
    data: {
      refundAmount?: number;
      paymentMethod?: 'CASH' | 'BANK_TRANSFER';
      projectBankAccountId?: string;
      paymentDate?: string;
    } = {},
  ) => {
    const response = await api.post<{ html: string }>(
      `/building-utilities/bookings/${id}/refund-deposit/preview`,
      data,
    );
    return response.data;
  },

  refundBookingDeposit: async (
    id: string,
    data: {
      refundAmount?: number;
      paymentMethod?: 'CASH' | 'BANK_TRANSFER';
      projectBankAccountId?: string;
      paymentDate?: string;
    } = {},
  ) => {
    const response = await api.patch<
      BuildingUtilityBooking & { apartmentLedgerEntryId?: string | null; journalNumber?: string | null }
    >(`/building-utilities/bookings/${id}/refund-deposit`, data);
    return response.data;
  },

  startBookingUsage: async (id: string) => {
    const response = await api.patch<BuildingUtilityBooking>(
      `/building-utilities/bookings/${id}/start`,
      {},
    );
    return response.data;
  },

  completeBooking: async (id: string) => {
    const response = await api.patch<BuildingUtilityBooking>(
      `/building-utilities/bookings/${id}/complete`,
      {},
    );
    return response.data;
  },

  getBookingActivityLogs: async (id: string) => {
    const response = await api.get(`/building-utilities/bookings/${id}/activity-logs`);
    return response.data;
  },
};

export type { BuildingUtilityGroup, BuildingUtility, BuildingUtilityBooking, CreateUtilityGroupRequest, UpdateUtilityGroupRequest }; 